import { ChangeEvent, FC, MouseEvent, useMemo, useState } from 'react';
import isEqual from 'react-fast-compare';
import { InputWrapperLarge, InputWrapperMedium } from '../../../common/FormElementWrappers/FormElementWrappers.styles';
import UnsavedChangesModal from '../../../common/UnsavedChangesModal/UnsavedChangesModal';
import Button from '../../../components/Button/Button';
import Dropdown from '../../../components/Dropdown/Dropdown';
import Input from '../../../components/Input/Input';
import MaskedInput from '../../../components/MaskedInput/MaskedInput';
import RadioGroup from '../../../components/RadioGroup/RadioGroup';
import { RadioGroupLayouts } from '../../../components/RadioGroup/RadioGroup.types';
import Textarea from '../../../components/Textarea/Textarea';
import { ALLOW_TOPPING_TYPE, AllowTopTypeKeys } from '../../../constants/ApiEnums';
import { SubTitle } from "../../Classes/ClassForm/ClassForm.styles";
import ImageForm from "../../../components/ImageForm/ImageForm";
import { readOnlyLabelTableText } from '../../../constants/GlobalTexts';
import {
  EditForm, EditFormActions, EditFormFields, FormFieldRow, FormRadioGroupContainer
} from "../../Bases/EditForm.styles";
import { Product } from '../../../services/product/product.types';
import { useUpdateProduct } from '../../../services/product/product.service';
import { useGenericErrorMessage } from '../../../utils/useGenericErrorMessage/useGenericErrorMessage';
import { statusOptions } from "../../Bases/EditBase/StatusOptions";

interface EditProductFormProps {
  product: Product;
  onSuccess: () => void;
}

interface EditProductFormValues {
  status: string;
  displayDescription: string;
  synopsis: string;
  sequence: string;
  validOnWeb: boolean;
  allowToppingTypeNo: string;
  serviceGuaranteed: boolean;
  overrideKitchenPrint: boolean;
  featureRefs: string;
  webDisplay: string;
}

const EditProductForm: FC<EditProductFormProps> = (
    {
      product,
      onSuccess
    }
) => {
  const classId = product.classNo;
  const productId = product.productNo;
  const {
    mutate: updateProduct,
    isLoading: updateProductIsLoading,
    isError: updateProductIsError,
    error: updateProductError
  } = useUpdateProduct({ classId, productId });

  useGenericErrorMessage([
    {
      isError: updateProductIsError,
      error: updateProductError
    }
  ]);

  const initialFormValues = useMemo<EditProductFormValues>(() => ({
    status: product.statusNo?.toString() ?? '',
    displayDescription: product.displayDesc,
    synopsis: product.synopsis,
    sequence: product.seqNo?.toString() ?? '',
    validOnWeb: product.validOnWeb,
    allowToppingTypeNo: product.allowToppingTypeNo?.toString() ?? '',
    serviceGuaranteed: product.serviceGuaranteed,
    overrideKitchenPrint: product.overrideKitchenPrint,
    featureRefs: product.featureRefs?.toString() ?? '',
    webDisplay: product.webDisplay?.toString() ?? ''
  }), [product]);

  const [formValues, setFormValues] = useState<EditProductFormValues>(initialFormValues);

  const [formErrors, setFormErrors] = useState({
    displayDescription: '',
    synopsis: '',
    sequence: '',
    featureRefs: '',
    webDisplay: ''
  });

  const unsavedChanges = !isEqual(initialFormValues, formValues);

  const updateFormValues = (
      event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormErrors({
      ...formErrors,
      [event.target.name]: ''
    });
    setFormValues({
      ...formValues,
      [event.target.name]: event.target.value
    });
  }

  const updateFormValuesFromRadioGroup = (
      event: ChangeEvent<HTMLInputElement>
  ) => {
    setFormErrors({
      ...formErrors,
      [event.target.name]: ''
    });
    setFormValues({
      ...formValues,
      [event.target.name]: event.target.value.includes('Yes')
    });
  }

  const handleSave = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    let error = false;
    let newFormErrors = {
      displayDescription: '',
      featureRefs: '',
      sequence: '',
      synopsis: '',
      webDisplay: ''
    };

    if (formValues.displayDescription?.length > 9) {
      error = true;
      newFormErrors.displayDescription = 'BOH Description must be 9 characters or less';
    }

    if (!formValues.displayDescription || formValues.displayDescription.length < 1) {
      error = true;
      newFormErrors.displayDescription = 'Please enter a BOH Description';
    }

    if (+formValues.featureRefs > 2147483646) {
      error = true;
      newFormErrors.featureRefs = 'Feature Refs is too long';
    }

    if ([null, undefined, -1, ''].includes(formValues.sequence)) {
      error = true;
      newFormErrors.sequence = 'Sequence Number is required.';
    }

    if (formValues.sequence?.length > 3) {
      error = true;
      newFormErrors.sequence = 'Sequence Number must be 3 characters or less';
    }

    if (formValues.synopsis?.length > 255) {
      error = true;
      newFormErrors.synopsis = 'Synopsis must be 255 characters or less';
    }

    if ([null, undefined, -1, ''].includes(formValues.webDisplay)) {
      error = true;
      newFormErrors.webDisplay = 'Web Display Sequence Number is required.';
    }

    if (+formValues.webDisplay > 2147483646) {
      error = true;
      newFormErrors.webDisplay = 'Web Display Sequence Number is too long';
    }

    if (error) {
      setFormErrors(newFormErrors);
      return;
    }

    updateProduct(
        {
          status: +formValues.status,
          displayDescription: formValues.displayDescription,
          synopsis: formValues.synopsis,
          sequence: +formValues.sequence,
          allowToppingTypeNo: +formValues.allowToppingTypeNo,
          serviceGuaranteed: formValues.serviceGuaranteed,
          overrideKitchenPrint: formValues.overrideKitchenPrint,
          validOnWeb: formValues.validOnWeb,
          featureRefs: +formValues.featureRefs,
          webDisplay: +formValues.webDisplay
        },
        {
          onSuccess: onSuccess
        }
    );
  };

  return (
      <EditForm>
        <EditFormFields>
          <FormFieldRow>
            <InputWrapperMedium>
              <Input
                  label={"Class Number"}
                  id={'classNo'}
                  value={product.classNo.toString()}
                  disabled
              />
            </InputWrapperMedium>
            <InputWrapperMedium>
              <Input
                  label={"Product Number"}
                  id={'productNo'}
                  value={product.productNo.toString()}
                  disabled
              />
            </InputWrapperMedium>
          </FormFieldRow>
          <FormFieldRow>
            <InputWrapperMedium>
              <MaskedInput
                  label={"Sequence Number"}
                  id={'sequence'}
                  name={'sequence'}
                  value={formValues.sequence.toString()}
                  isAllowed={(values) => +values.value < 2147483648}
                  onChange={updateFormValues}
                  onValueChange={(_values, source) => source.event && updateFormValues(source.event)}
                  errorText={formErrors.sequence}
              />
            </InputWrapperMedium>
          </FormFieldRow>
          <FormFieldRow>
            <InputWrapperLarge>
              <Input
                  label={"Short Description"}
                  id={'description'}
                  value={product.description}
                  disabled
              />
            </InputWrapperLarge>
            <InputWrapperLarge>
              <Input
                  label={"Long Description"}
                  id={'longDescription'}
                  value={product.longDescription}
                  disabled
              />
            </InputWrapperLarge>
          </FormFieldRow>
          <FormFieldRow>
            <Textarea
                label={"Synopsis"}
                id={'synopsis'}
                name={'synopsis'}
                value={formValues.synopsis}
                onChange={updateFormValues}
                errorText={formErrors.synopsis}
            />
          </FormFieldRow>
          <FormFieldRow>
            <InputWrapperLarge>
              <Input
                  label={"BOH Description"}
                  id={'displayDescription'}
                  name={'displayDescription'}
                  value={formValues.displayDescription}
                  onChange={updateFormValues}
                  errorText={formErrors.displayDescription}
              />
            </InputWrapperLarge>
          </FormFieldRow>
          <FormFieldRow>
            <InputWrapperLarge>
              <Dropdown
                  label={"Status"}
                  id={'status'}
                  name={'status'}
                  options={statusOptions}
                  value={formValues.status.toString()}
                  onChange={updateFormValues}
              />
            </InputWrapperLarge>
            <InputWrapperLarge>
              <Dropdown
                  label={"Allow Topping Type Number"}
                  id={'allowToppingTypeNo'}
                  name={'allowToppingTypeNo'}
                  options={Object.keys(ALLOW_TOPPING_TYPE).map((key: any) => {
                    return { value: key, label: `${ALLOW_TOPPING_TYPE[key as AllowTopTypeKeys]}` };
                  })}
                  value={formValues.allowToppingTypeNo}
                  onChange={updateFormValues}
              />
            </InputWrapperLarge>
          </FormFieldRow>
          <FormFieldRow>
            <InputWrapperMedium>
              <Input
                  label={"Max Toppings"}
                  id={'maxToppings'}
                  name={'maxToppings'}
                  value={product.maxTopping?.toString()}
                  disabled
              />
            </InputWrapperMedium>
            <InputWrapperMedium>
              <Input
                  label={"Free Toppings"}
                  id={'freeToppings'}
                  name={'freeToppings'}
                  value={product.maxFreeTopping?.toString()}
                  disabled
              />
            </InputWrapperMedium>
          </FormFieldRow>
          <FormFieldRow>
            <FormRadioGroupContainer>
              <RadioGroup
                  label={"Service Guaranteed"}
                  id={'serviceGuaranteed'}
                  options={[
                    {
                      label: 'Yes',
                      id: 'serviceGuaranteedYes'
                    },
                    {
                      label: 'No',
                      id: 'serviceGuaranteedNo'
                    }
                  ]}
                  layout={RadioGroupLayouts.Row}
                  selected={formValues.serviceGuaranteed ? 'serviceGuaranteedYes' : 'serviceGuaranteedNo'}
                  onChange={updateFormValuesFromRadioGroup}
              />
            </FormRadioGroupContainer>
            <FormRadioGroupContainer>
              <RadioGroup
                  label={"Override Kitchen Print"}
                  id={'overrideKitchenPrint'}
                  options={[
                    {
                      id: 'overrideKitchenYes',
                      label: 'Yes'
                    },
                    {
                      id: 'overrideKitchenNo',
                      label: 'No'
                    }
                  ]}
                  layout={RadioGroupLayouts.Row}
                  selected={formValues.overrideKitchenPrint ? 'overrideKitchenYes' : 'overrideKitchenNo'}
                  onChange={updateFormValuesFromRadioGroup}
              />
            </FormRadioGroupContainer>
            <FormRadioGroupContainer>
              <RadioGroup
                  label={"Valid on Web?"}
                  id={'validOnWeb'}
                  options={[
                    {
                      id: 'validOnWebYes',
                      label: 'Yes'
                    },
                    {
                      id: 'validOnWebNo',
                      label: 'No'
                    }
                  ]}
                  layout={RadioGroupLayouts.Row}
                  selected={formValues.validOnWeb ? 'validOnWebYes' : 'validOnWebNo'}
                  onChange={updateFormValuesFromRadioGroup}
              />
            </FormRadioGroupContainer>
          </FormFieldRow>
          <FormFieldRow>
            <InputWrapperMedium>
              <MaskedInput
                  label={"Feature Refs"}
                  id={'featureRefs'}
                  name={'featureRefs'}
                  value={formValues.featureRefs}
                  format="##########"
                  onChange={updateFormValues}
                  onValueChange={(_values, source) => source.event && updateFormValues(source.event)}
                  errorText={formErrors.featureRefs}
                  testId={'featureRefsMaskedInput'}
              />
            </InputWrapperMedium>
            <InputWrapperMedium>
              <Input
                  label={"Mnemonic"}
                  id={'mnemonic'}
                  name={'mnemonic'}
                  value={product.mnemonic}
                  disabled
              />
            </InputWrapperMedium>
            <InputWrapperMedium>
              <MaskedInput
                  label={"Web Display Sequence No."}
                  id={'webDisplay'}
                  name={'webDisplay'}
                  value={formValues.webDisplay.toString()}
                  decimalScale={0}
                  allowNegative={false}
                  onChange={updateFormValues}
                  onValueChange={(_values, source) => source.event && updateFormValues(source.event)}
                  errorText={formErrors.webDisplay}
                  testId={'webDisplayMaskedInput'}
              />
            </InputWrapperMedium>
          </FormFieldRow>
          <ImageForm
              attribute={'prod'}
              resourceNo={[classId, productId]}
          />
          <SubTitle>{readOnlyLabelTableText}</SubTitle>
        </EditFormFields>
        <EditFormActions>
          <Button
              label={"Save"}
              onClick={handleSave}
              disabled={!unsavedChanges}
              isLoading={updateProductIsLoading}
          />
        </EditFormActions>
        <UnsavedChangesModal unsavedChangesExist={unsavedChanges}/>
      </EditForm>
  );
};

export default EditProductForm;